import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  makeStyles,
  TextField,
  CircularProgress,
  useTheme, 
  useMediaQuery,
  Chip
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { green } from '@material-ui/core/colors';
import { STATUS_API } from 'src/app/constant/config';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { updateServicePackage, createServicePackage } from 'src/features/servicePackageSlice';
import { getListDeviceVersion } from 'src/features/versionDeviceSlice';
import CustomDialog from 'src/app/components/CustomDialog';

import { getListDeviceType } from 'src/features/deviceTypeSlice';

const EditServicePackage = ({ open, onClose, servicePackage }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const statusCreate = useSelector(
    state => state.servicePackageSlice.statusCreate
  );
  const listDeviceType = useSelector(
    state => state.deviceTypeSlice.listDeviceType
  );
  const listDeviceVersion = useSelector(
    state => state.versionDeviceSlice.listDeviceVersion
  );

  useEffect(() => {
    dispatch(getListDeviceType({ page_size: 1000 }));
    dispatch(getListDeviceVersion({ page_size: 1000 }));
  }, [dispatch]);

  const deviceTypeOptions = React.useMemo(
    () => {
      return (listDeviceType || []).map(type => ({
        id: Number(type.id),
        name: type.name
      }));
    },
    [listDeviceType]
  );

  const versionOptions = React.useMemo(() => {
    const versions = (listDeviceVersion || []).map(item => item.version).filter(Boolean);
    const uniqueVersions = [...new Set(versions)];
    return uniqueVersions.map(v => ({ id: v, name: v }));
  }, [listDeviceVersion]);

  const handleSubmit = async data => {
    if (!servicePackage || !servicePackage?.id) return;
    
    const selectedIds = data.device_type_ids.map(Number).filter(id => !isNaN(id) && id > 0);
    if (selectedIds.length === 0) return;

    // 1. Cập nhật gói hiện tại với loại thiết bị đầu tiên
    const updateData = {
      name: data.name,
      version: data.version,
      cost: Number(data.cost),
      day_limit: Number(data.day_limit),
      description: data.description,
      type: data.type,
      effective_date: data.effective_date || null,
      end_date: data.end_date || null,
      device_type_id: selectedIds[0]
    };

    await dispatch(updateServicePackage({ id: servicePackage.id, data: updateData }));

    // 2. Tạo mới các gói cước cho những loại thiết bị được chọn thêm
    if (selectedIds.length > 1) {
      for (const devTypeId of selectedIds.slice(1)) {
        const createData = {
          device_type_id: devTypeId,
          name: data.name,
          version: data.version,
          cost: Number(data.cost),
          day_limit: Number(data.day_limit),
          description: data.description,
          type: data.type,
          effective_date: data.effective_date || null,
          end_date: data.end_date || null
        };
        await dispatch(createServicePackage(createData));
      }
    }
    
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const formatNumber = value => {
    if (value == null || value === '') return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const parseNumber = value => {
    return value.replace(/\./g, '');
  };

  const foundDeviceType = (listDeviceType || []).find(
    type =>
      type.name &&
      servicePackage?.device_type_name &&
      type.name.toLowerCase() === servicePackage.device_type_name.toLowerCase()
  );
  const resolvedDeviceTypeId =
    servicePackage?.deviceType?.id !== undefined ? servicePackage?.deviceType?.id :
    servicePackage?.device_type_id !== undefined ? servicePackage?.device_type_id :
    foundDeviceType?.id;

  const initialDeviceTypeIds = React.useMemo(() => {
    if (!resolvedDeviceTypeId) {
      return [];
    }
    return [Number(resolvedDeviceTypeId)].filter(id => !isNaN(id));
  }, [resolvedDeviceTypeId]);

  return (
    <CustomDialog
      title="Chỉnh sửa gói dịch vụ"
      open={open}
      onClose={handleClose}
    >
      <div>
        <Formik
          initialValues={{
            name: servicePackage?.name || '',
            version: servicePackage?.version || '',
            cost: servicePackage?.cost || '',
            day_limit: servicePackage?.day_limit || '',
            description: servicePackage?.description || '',
            device_type_ids: initialDeviceTypeIds,
            type: servicePackage?.type || 'GIA_HAN',
            effective_date: servicePackage?.effective_date ? servicePackage.effective_date.slice(0, 10) : '',
            end_date: servicePackage?.end_date ? servicePackage.end_date.slice(0, 10) : ''
          }}
          enableReinitialize
          validationSchema={Yup.object().shape({
            device_type_ids: Yup.array()
              .min(1, 'Vui lòng chọn ít nhất 1 loại thiết bị')
              .required('Tên loại thiết bị không được để trống'),
            version: Yup.string()
              .required('Vui lòng chọn phiên bản')
              .max(100),
            name: Yup.string()
              .max(100)
              .required('Tên gói dịch vụ không được để trống'),
            cost: Yup.number().required('Giá gói dịch vụ không được để trống'),
            day_limit: Yup.number().required(
              'Hạn gói dịch vụ không được để trống'
            ),
            type: Yup.string().required('Loại gói cước không được để trống'),
            effective_date: Yup.date().nullable(),
            end_date: Yup.date().nullable(),
            description: Yup.string()
              .max(100)
              .required('Mô tả dịch vụ không được để trống')
          })}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            touched,
            values
          }) => (
            <form onSubmit={handleSubmit} className={classes.formContainer}>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                    Tên loại thiết bị <span className="text-danger">*</span>
                  </InputLabel>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    size="small"
                    options={deviceTypeOptions || []}
                    getOptionLabel={option => option.name}
                    getOptionSelected={(option, val) => String(option.id) === String(val.id)}
                    value={
                      (deviceTypeOptions || []).filter(option => values.device_type_ids?.map(Number).includes(Number(option.id)))
                    }
                    onChange={(e, selectedOptions) => {
                      const ids = selectedOptions.map(opt => Number(opt.id)).filter(id => !isNaN(id));
                      setFieldValue('device_type_ids', ids);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Chọn loại thiết bị"
                        name="device_type_ids"
                        margin="none"
                        className="input-no-fieldset"
                        error={Boolean(
                          touched.device_type_ids && errors.device_type_ids
                        )}
                        helperText={
                          touched.device_type_ids && errors.device_type_ids
                        }
                      />
                    )}
                    className="input-no-fieldset"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                    Tên gói dịch vụ <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.name && errors.name)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.name && errors.name}
                    margin="none"
                    name="name"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    placeholder="Nhập tên gói dịch vụ"
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                    Phiên bản <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    select
                    error={Boolean(touched.version && errors.version)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.version && errors.version}
                    margin="none"
                    name="version"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.version || ''}
                    variant="outlined"
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="" disabled>
                      -- Chọn phiên bản --
                    </option>
                    {versionOptions.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                    Giá gói dịch vụ <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.cost && errors.cost)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.cost && errors.cost}
                    margin="none"
                    name="cost"
                    size="small"
                    onBlur={handleBlur}
                    onChange={e => {
                      const raw = parseNumber(e.target.value);
                      if (!isNaN(raw)) {
                        setFieldValue('cost', raw);
                      }
                    }}
                    value={formatNumber(values.cost)}
                    placeholder="Nhập giá gói dịch vụ"
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                    Hạn gói dịch vụ (Ngày){' '}
                    <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.day_limit && errors.day_limit)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.day_limit && errors.day_limit}
                    margin="none"
                    name="day_limit"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.day_limit}
                    placeholder="Nhập hạn gói dịch vụ"
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                    Loại gói cước <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    select
                    error={Boolean(touched.type && errors.type)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.type && errors.type}
                    margin="none"
                    name="type"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.type}
                    variant="outlined"
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="GIA_HAN">Gia hạn</option>
                    <option value="KICH_HOAT">Kích hoạt</option>
                  </TextField>
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>Ngày hiệu lực</InputLabel>
                  <TextField
                    type="date"
                    error={Boolean(touched.effective_date && errors.effective_date)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.effective_date && errors.effective_date}
                    margin="none"
                    name="effective_date"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.effective_date || ''}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>Ngày hết hạn</InputLabel>
                  <TextField
                    type="date"
                    error={Boolean(touched.end_date && errors.end_date)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.end_date && errors.end_date}
                    margin="none"
                    name="end_date"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.end_date || ''}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item md={12} xs={12}>
                  <InputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                    Mô tả <span className="text-danger">*</span>
                  </InputLabel>
                  <TextField
                    error={Boolean(touched.description && errors.description)}
                    className="input-no-fieldset"
                    fullWidth
                    helperText={touched.description && errors.description}
                    margin="none"
                    name="description"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    placeholder="Nhập mô tả"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              <Box my={3}>
                <div className={classes.groupButtonSubmit}>
                  <div className={classes.wrapper}>
                    <Button
                      style={{
                        ...(isMobile && { maxWidth: 90 }),
                        marginRight: '10px',
                        textTranform: 'none !important'
                      }}
                      onClick={() => onClose()}
                      className="btn-main btn-plain mx-3"
                      color="primary"
                      size="large"
                      variant="contained"
                    >
                      Thoát
                    </Button>
                    <Button
                      style={{
                        ...(isMobile && { maxWidth: 120 }),
                        marginRight: '10px',
                        textTranform: 'none !important'
                      }}
                      className="btn-main mx-3"
                      color="primary"
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      {'Lưu lại'}
                    </Button>
                    {statusCreate === STATUS_API.PENDING && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </div>
                </div>
              </Box>
            </form>
          )}
        </Formik>
      </div>
    </CustomDialog>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  groupButtonSubmit: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '15px',

    '& .left-button': {
      display: 'flex'
    }
  },
  wrapper: {
    position: 'relative'
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  formContainer: {
    '& .MuiOutlinedInput-root': {
      minHeight: '40px',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '14px',
      padding: '10.5px 14px',
      boxSizing: 'border-box'
    },
    '& select.MuiSelect-select': {
      fontSize: '14px',
      paddingTop: '10.5px',
      paddingBottom: '10.5px',
      height: 'auto',
      boxSizing: 'border-box'
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: '3px 8px !important',
      minHeight: '40px'
    }
  },
  customOutline: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
      borderColor: 'rgba(31, 31, 31, 0.14)',
      borderRadius: '4px'
    }
  }
}));

export default EditServicePackage;
